import {
  INCOME_TAX,
  INCOME_FREQUENCY,
  INCOME_FREQUENCY_TEXT,
  REPAYMENT_THRESHOLD_RATE,
} from 'Common/constants/incomeTax';
import { getParsed } from 'Common/utilities/math';
import { getMoneyForm } from 'Common/utilities/currency';

const divideValueByFrequency = (value, frequency) => {
  return getMoneyForm(value / INCOME_FREQUENCY[frequency]);
};

const getSalaryBracket = (taxYear, taxableIncome) => {
  return INCOME_TAX[taxYear].taxBrackets.find((bracket) => {
    return bracket.from <= taxableIncome && bracket.to >= taxableIncome;
  });
};

const getBracket = (brackets, taxableIncome) => {
  return brackets.find((bracket) => {
    return bracket.from <= taxableIncome && bracket.to >= taxableIncome;
  });
};

export function getAnnualTaxableIncome(calculationObject, frequency) {
  const annualTaxableIncome = getMoneyForm(
    getParsed(calculationObject.taxableIncome),
  );

  switch (calculationObject.frequency) {
    case INCOME_FREQUENCY_TEXT.DAILY:
      if (frequency !== INCOME_FREQUENCY_TEXT.ANNUALLY) {
        return (
          annualTaxableIncome *
          calculationObject.daysPerWeek *
          INCOME_FREQUENCY.WEEKLY
        );
      }

      return annualTaxableIncome * calculationObject.daysPerYear;
    case INCOME_FREQUENCY_TEXT['HOURLY/WEEK']:
      if (frequency !== INCOME_FREQUENCY_TEXT.ANNUALLY) {
        return (
          annualTaxableIncome *
          calculationObject.hoursPerWeek *
          INCOME_FREQUENCY.WEEKLY
        );
      }

      return (
        annualTaxableIncome *
        calculationObject.hoursPerWeek *
        calculationObject.weeksPerYear
      );
    case INCOME_FREQUENCY_TEXT['HOURLY/DAY']:
      if (frequency !== INCOME_FREQUENCY_TEXT.ANNUALLY) {
        return (
          annualTaxableIncome *
          calculationObject.hoursPerDay *
          calculationObject.daysPerWeek *
          INCOME_FREQUENCY.WEEKLY
        );
      }

      return (
        annualTaxableIncome *
        calculationObject.hoursPerDay *
        calculationObject.daysPerYear
      );
    default:
      return (
        annualTaxableIncome * INCOME_FREQUENCY[calculationObject.frequency]
      );
  }
}

export const getLowIncomeTaxOffsetBracket = (brackets, taxableIncome) => {
  return brackets.find(
    (bracket) => taxableIncome >= bracket.from && taxableIncome <= bracket.to,
  );
};

export function getLowIncomeTaxOffset(taxableIncome, taxData) {
  const lito = taxData.lowIncomeTaxOffset.useBracket
    ? getLowIncomeTaxOffsetBracket(
        taxData.lowIncomeTaxOffset.brackets,
        taxableIncome,
      )
    : { ...taxData.lowIncomeTaxOffset };
  const isWithinOffsetRange =
    taxableIncome < lito?.maxThreshold && taxableIncome;

  if (!isWithinOffsetRange) {
    return 0;
  }

  if (taxableIncome <= lito.minThreshold) {
    return lito.baseAmount;
  }

  return (
    lito.baseAmount -
    (lito.reductionRate / 100) * (taxableIncome - lito.minThreshold)
  );
}

export function getLowAndMiddleIncomeTaxOffset(taxableIncome, taxData) {
  const lamito = { ...taxData.lowAndMiddleIncomeTaxOffset };
  const offsetBracket = getBracket(lamito.brackets, taxableIncome);

  if (!offsetBracket) {
    return 0;
  }

  const bracketLowerLimit = offsetBracket.from - 1;
  return (
    offsetBracket.baseAmount +
    (taxableIncome - bracketLowerLimit) * (offsetBracket.multiplier / 100)
  );
}

const getTaxOffset = (calculationObject, frequency, taxableIncome, taxData) => {
  const isLamitoApplied =
    taxData.lowAndMiddleIncomeTaxOffset &&
    taxData.lowAndMiddleIncomeTaxOffset.minYearEffective <=
      calculationObject.taxYear &&
    taxData.lowAndMiddleIncomeTaxOffset.maxYearEffective >=
      calculationObject.taxYear;
  const lowIncomeTaxOffset = getLowIncomeTaxOffset(taxableIncome, taxData);
  const lowAndMiddleIncomeTaxOffset = isLamitoApplied
    ? getLowAndMiddleIncomeTaxOffset(taxableIncome, taxData)
    : 0;

  if (
    !calculationObject.withholdTaxOffset &&
    frequency !== INCOME_FREQUENCY_TEXT.ANNUALLY
  ) {
    return 0;
  }

  return lowIncomeTaxOffset + lowAndMiddleIncomeTaxOffset;
};

export const getHecsHelpRepayment = (taxYear, taxableIncome) => {
  const repaymentThreshold = REPAYMENT_THRESHOLD_RATE[taxYear];
  if (!taxableIncome || isNaN(taxableIncome) || !repaymentThreshold) {
    return 0;
  }

  const rate =
    repaymentThreshold.find(
      (bracket) => bracket.from <= taxableIncome && bracket.to >= taxableIncome,
    )?.rate ?? 0;

  return taxableIncome * rate * 0.01;
};

export function calculatePay(calculationObject, frequency) {
  const { taxYear } = calculationObject;

  const annualIncome = getAnnualTaxableIncome(calculationObject, frequency);

  const superAnnuationRate = calculationObject.superannuationRate / 100;
  const taxableIncome = calculationObject.includeSuperannuation
    ? annualIncome / (1 + superAnnuationRate)
    : annualIncome;
  const superannuation = taxableIncome * superAnnuationRate;

  const taxData = INCOME_TAX[taxYear];
  const taxFreeBracket = taxData.taxBrackets[0];
  const isTaxFree =
    taxableIncome >= taxFreeBracket.from && taxableIncome <= taxFreeBracket.to;

  if (isTaxFree) {
    return {
      frequency,
      superannuation: divideValueByFrequency(superannuation, frequency),
      pay: divideValueByFrequency(taxableIncome, frequency),
      taxableIncome: divideValueByFrequency(taxableIncome, frequency),
      totalTaxes: 0,
      incomeTax: 0,
      medicare: 0,
      taxOffset: 0,
    };
  }

  // calculate tax breakdown based on annual taxable income
  const taxBracket = getSalaryBracket(taxYear, taxableIncome);
  const bracketLowerLimit = taxBracket.from - 1;

  const { ANNUALLY } = INCOME_FREQUENCY_TEXT;
  const computedIncomeTax =
    taxBracket.baseTax +
    (taxableIncome - bracketLowerLimit) * (taxBracket.multiplier / 100);
  const incomeTax =
    frequency === ANNUALLY ? Math.ceil(computedIncomeTax) : computedIncomeTax;

  const medicareDeduction =
    taxableIncome * (taxData.medicareDeductionRate / 100);
  const taxOffset = getTaxOffset(
    calculationObject,
    frequency,
    taxableIncome,
    taxData,
  );
  const hecsHelpCalculation = calculationObject.includeHecsHelp
    ? getHecsHelpRepayment(taxYear, taxableIncome)
    : 0;

  const totalTaxesComp =
    incomeTax + medicareDeduction + hecsHelpCalculation - taxOffset;
  const totalTaxes = ANNUALLY ? Math.round(totalTaxesComp) : totalTaxesComp;
  const pay = taxableIncome - totalTaxes;

  const dividedTotalTax = divideValueByFrequency(totalTaxes, frequency);
  const totalTaxesFix = dividedTotalTax < 1 ? 0 : dividedTotalTax;

  return {
    frequency,
    taxOffset: divideValueByFrequency(taxOffset, frequency),
    superannuation: divideValueByFrequency(superannuation, frequency),
    pay: divideValueByFrequency(pay, frequency),
    taxableIncome: divideValueByFrequency(taxableIncome, frequency),
    totalTaxes: totalTaxesFix,
    incomeTax: divideValueByFrequency(incomeTax, frequency),
    medicare: divideValueByFrequency(medicareDeduction, frequency),
    hecsHelpRepayment: divideValueByFrequency(hecsHelpCalculation, frequency),
  };
}
